/**
 * Blood Health AI — cart + purchase flow.
 * Enhances elements marked with data-bh-* attributes; the buy mode is decided
 * server-side in Liquid, so this file never branches on settings. With JS
 * disabled every hook falls back to a plain link or a native form POST.
 */
(() => {
  const drawer = document.getElementById('bh-cart-drawer'); // null in checkout mode
  const i18nEl = document.querySelector('[data-bh-cart-i18n]');
  let i18n = {};
  try {
    i18n = i18nEl ? JSON.parse(i18nEl.textContent) : {};
  } catch (e) {
    i18n = {};
  }

  const currency =
    (drawer && drawer.dataset.currency) ||
    document.querySelector('[data-bh-variants]')?.dataset.currency ||
    'USD';
  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
      // Western digits in every locale (Arabic included), matching the
      // server-rendered prices.
      numberingSystem: 'latn',
    }).format(cents / 100);

  // --- Cart API ---
  // Locale-aware root ('/' for the default language, '/ar/' etc. otherwise) so
  // Shopify returns cart error messages in the shopper's language.
  const routesRoot = window.Shopify?.routes?.root || '/';
  const jsonOrThrow = async (res) => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body.description || body.message || i18n.addError || 'Cart error');
    }
    return body;
  };
  const getCart = () => fetch(`${routesRoot}cart.js`).then(jsonOrThrow);
  const addItem = (payload) =>
    fetch(`${routesRoot}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(jsonOrThrow);
  const addForm = (form) =>
    fetch(`${routesRoot}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    }).then(jsonOrThrow);
  const updateDiscounts = (discount) =>
    fetch(`${routesRoot}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount }),
    }).then(jsonOrThrow);
  const checkoutPermalink = (form, discountCodes) => {
    const data = new FormData(form);
    const variantId = parseInt(data.get('id'), 10);
    const quantity = Math.max(1, parseInt(data.get('quantity') || '1', 10) || 1);
    if (!variantId) throw new Error(i18n.addError || 'Cart error');

    const params = new URLSearchParams();
    params.set('discount', discountCodes);

    const properties = {};
    data.forEach((value, key) => {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match && typeof value === 'string' && value) properties[match[1]] = value;
    });
    if (Object.keys(properties).length) {
      const bytes = new TextEncoder().encode(JSON.stringify(properties));
      const encoded = btoa(Array.from(bytes, (byte) => String.fromCharCode(byte)).join(''));
      params.set('properties', encoded);
    }

    const query = params.toString();
    return `${i18n.localeRoot || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart/${variantId}:${quantity}${query ? `?${query}` : ''}`;
  };
  const changeLine = (key, quantity) =>
    fetch(`${routesRoot}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    }).then(jsonOrThrow);

  // --- Badges (header count bubble) ---
  const updateBadges = (count) => {
    document.querySelectorAll('[data-bh-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    if (drawer) {
      const inline = drawer.querySelector('[data-bh-cart-count-inline]');
      if (inline) inline.textContent = count > 0 ? `(${count})` : '';
    }
  };

  // --- Drawer ---
  let busy = false;
  let lastTrigger = null;
  let fetched = false;
  let cartState = null;
  // Monotonic token: a render only applies if no newer fetch superseded it,
  // so a stale /cart.js response can never overwrite a post-add render.
  let cartSeq = 0;
  let inflight = null;

  const setBusy = (state) => {
    busy = state;
    if (drawer) drawer.setAttribute('aria-busy', state ? 'true' : 'false');
  };

  const setTriggerBusy = (trigger, state) => {
    if (!trigger) return;
    trigger.toggleAttribute('disabled', state);
    trigger.setAttribute('aria-disabled', state ? 'true' : 'false');
    trigger.setAttribute('aria-busy', state ? 'true' : 'false');
  };

  // Loading row shown only before the first successful render — afterwards the
  // existing aria-busy dimming covers in-place refreshes.
  const setLoading = (state) => {
    if (!drawer) return;
    const el = drawer.querySelector('[data-bh-cart-loading]');
    if (el) el.hidden = !state;
  };

  // Fetch + render behind the seq guard. Plain refreshes share one in-flight
  // request; `force` (post-mutation) always issues a fresh one.
  const refreshCart = (force) => {
    if (force || !inflight) {
      const seq = ++cartSeq;
      const p = getCart()
        .then((cart) => {
          fetched = true;
          if (seq === cartSeq) render(cart);
          return cart;
        })
        .finally(() => {
          if (inflight === p) inflight = null;
        });
      inflight = p;
    }
    return inflight;
  };

  const showError = (msg) => {
    if (!drawer) return;
    const region = drawer.querySelector('[data-bh-cart-error]');
    region.textContent = msg;
    region.hidden = false;
  };

  const clearError = () => {
    if (!drawer) return;
    const region = drawer.querySelector('[data-bh-cart-error]');
    region.hidden = true;
  };

  const emptyCart = () => ({
    item_count: 0,
    items: [],
    items_subtotal_price: 0,
    total_price: 0,
  });

  const copyCart = (cart = cartState) => {
    const source = cart || emptyCart();
    return {
      ...source,
      items: (source.items || []).map((item) => ({ ...item })),
    };
  };

  const addMetaFromForm = (form) => {
    const data = new FormData(form);
    const id = parseInt(data.get('id'), 10);
    if (!id) return null;
    return {
      id,
      quantity: Math.max(1, parseInt(data.get('quantity') || '1', 10) || 1),
      productTitle: form.dataset.bhProductTitle || 'Pressure Compass',
      variantTitle: form.dataset.bhVariantTitle || 'Default Title',
      image: form.dataset.bhProductImage || '',
      url: form.dataset.bhProductUrl || window.location.pathname,
      unitPrice: Math.max(0, parseInt(form.dataset.bhProductPrice || '0', 10) || 0),
    };
  };

  const addMetaFromButton = (button) => {
    const id = parseInt(button.dataset.variantId, 10);
    if (!id) return null;
    const matchingForm = Array.from(document.querySelectorAll('[data-bh-atc-form]')).find((form) => {
      return parseInt(new FormData(form).get('id'), 10) === id;
    });
    const meta = matchingForm ? addMetaFromForm(matchingForm) : null;
    if (meta) {
      meta.quantity = Math.max(1, parseInt(button.dataset.quantity || '1', 10) || 1);
      return meta;
    }
    if (!button.dataset.productTitle) return null;
    return {
      id,
      quantity: Math.max(1, parseInt(button.dataset.quantity || '1', 10) || 1),
      productTitle: button.dataset.productTitle,
      variantTitle: button.dataset.variantTitle || 'Default Title',
      image: button.dataset.productImage || '',
      url: button.dataset.productUrl || window.location.pathname,
      unitPrice: Math.max(0, parseInt(button.dataset.productPrice || '0', 10) || 0),
    };
  };

  const addedLineFromResponse = (response, id) => {
    const items = Array.isArray(response?.items) ? response.items : [response];
    return items.find((item) => String(item?.id) === String(id)) || items.find(Boolean) || null;
  };

  // Paint the requested item immediately from product data already in the
  // page. The /cart/add.js response replaces the temporary line, then a quiet
  // /cart.js refresh reconciles discounts and any server-side cart rules.
  const cartWithAddition = (baseCart, meta, serverLine) => {
    const next = copyCart(baseCart);
    const index = next.items.findIndex((item) => String(item.id) === String(meta.id));
    const existing = index >= 0 ? next.items[index] : null;
    const unitPrice =
      serverLine?.final_price ??
      serverLine?.price ??
      existing?.final_price ??
      existing?.price ??
      meta.unitPrice;
    const quantity = (existing?.quantity || 0) + meta.quantity;
    const line = {
      ...(existing || {}),
      ...(serverLine || {}),
      id: serverLine?.id || meta.id,
      key: serverLine?.key || existing?.key || `bh-pending-${meta.id}`,
      product_title: serverLine?.product_title || meta.productTitle,
      variant_title: serverLine?.variant_title || meta.variantTitle,
      quantity,
      final_line_price: unitPrice * quantity,
      image: serverLine?.image || meta.image,
      url: serverLine?.url || meta.url,
      __bhPending: !serverLine,
    };

    if (index >= 0) next.items[index] = line;
    else next.items.unshift(line);

    const baseCount =
      Number.isFinite(baseCart.item_count)
        ? baseCart.item_count
        : baseCart.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const baseSubtotal =
      Number.isFinite(baseCart.items_subtotal_price) ? baseCart.items_subtotal_price : 0;
    next.item_count = baseCount + meta.quantity;
    next.items_subtotal_price = baseSubtotal + unitPrice * meta.quantity;
    return next;
  };

  const lineItem = (item) => {
    const li = document.createElement('li');
    li.className = 'bh-cart-item';
    li.dataset.lineKey = item.key;
    const pending = Boolean(item.__bhPending);

    const media = document.createElement('div');
    media.className = 'bh-cart-item__media';
    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image + (item.image.includes('?') ? '&' : '?') + 'width=160';
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 160;
      img.height = 160;
      media.appendChild(img);
    }

    const body = document.createElement('div');

    const title = document.createElement('p');
    title.className = 'bh-cart-item__title';
    title.textContent = item.product_title;
    body.appendChild(title);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('p');
      variant.className = 'bh-cart-item__variant';
      variant.textContent = item.variant_title;
      body.appendChild(variant);
    }

    const row = document.createElement('div');
    row.className = 'bh-cart-item__row';

    const stepper = document.createElement('span');
    stepper.className = 'bh-cart-item__stepper';
    const btn = (delta, label, text) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bh-cart-item__step';
      b.dataset.bhQtyChange = delta;
      b.setAttribute('aria-label', label || '');
      b.textContent = text;
      b.disabled = pending;
      return b;
    };
    stepper.appendChild(btn('-1', i18n.decrease, '−'));
    const qty = document.createElement('span');
    qty.className = 'bh-cart-item__qty';
    qty.textContent = item.quantity;
    stepper.appendChild(qty);
    stepper.appendChild(btn('1', i18n.increase, '+'));

    const price = document.createElement('span');
    price.className = 'bh-cart-item__price';
    price.textContent = money(item.final_line_price);

    row.appendChild(stepper);
    row.appendChild(price);
    body.appendChild(row);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'bh-cart-item__remove';
    remove.dataset.bhQtyChange = '0';
    remove.textContent = i18n.remove || 'Remove';
    remove.disabled = pending;
    body.appendChild(remove);

    li.appendChild(media);
    li.appendChild(body);
    return li;
  };

  const render = (cart) => {
    if (!drawer) return;
    const state = copyCart(cart);
    cartState = state;
    const list = drawer.querySelector('[data-bh-cart-items]');
    const empty = drawer.querySelector('[data-bh-cart-empty]');
    const foot = drawer.querySelector('[data-bh-cart-foot]');
    const subtotal = drawer.querySelector('[data-bh-cart-subtotal]');

    list.textContent = '';
    state.items.forEach((item) => list.appendChild(lineItem(item)));

    const has = state.item_count > 0;
    empty.hidden = has;
    foot.hidden = !has;
    subtotal.textContent = money(state.items_subtotal_price);
    updateBadges(state.item_count);
  };

  const openDrawer = (trigger) => {
    if (!drawer || drawer.open) return;
    lastTrigger = trigger || null;
    clearError();
    drawer.showModal();
    // When busy, an add/change chain is already in charge of the fetch,
    // render, and spinner — starting a second chain here would race it.
    if (!fetched && !busy) {
      setLoading(true);
      refreshCart()
        .catch((e) => showError(e.message))
        .finally(() => setLoading(false));
    }
  };

  const closeDrawer = () => {
    if (!drawer || !drawer.open) return;
    drawer.close();
  };

  if (drawer) {
    drawer.addEventListener('close', () => {
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
      lastTrigger = null;
    });

    drawer.addEventListener('click', (e) => {
      // Backdrop clicks target the dialog element itself
      if (e.target === drawer) {
        closeDrawer();
        return;
      }
      const closeBtn = e.target.closest('[data-bh-cart-close]');
      if (closeBtn) {
        closeDrawer();
        return;
      }
      const step = e.target.closest('[data-bh-qty-change]');
      if (step && !busy) {
        const line = step.closest('[data-line-key]');
        if (!line) return;
        const current = parseInt(line.querySelector('.bh-cart-item__qty')?.textContent || '1', 10);
        const delta = parseInt(step.dataset.bhQtyChange, 10);
        const next = delta === 0 ? 0 : Math.max(0, current + delta);
        setBusy(true);
        clearError();
        changeLine(line.dataset.lineKey, next)
          .then((cart) => {
            cartSeq += 1; // /cart/change.js returns the full cart; invalidate stragglers
            fetched = true;
            render(cart);
          })
          .catch((e2) => showError(e2.message))
          .finally(() => setBusy(false));
      }
    });

    // The server embeds the current cart in the page, so the drawer and badge
    // are ready before the shopper can click. Fetch only as a compatibility
    // fallback if that snapshot is unavailable.
    const initialCartEl = document.querySelector('[data-bh-initial-cart]');
    try {
      const initialCart = initialCartEl ? JSON.parse(initialCartEl.textContent) : null;
      if (initialCart && Array.isArray(initialCart.items)) {
        fetched = true;
        render(initialCart);
      } else {
        refreshCart().catch(() => {});
      }
    } catch (e) {
      refreshCart().catch(() => {});
    }

    // bfcache restores resurrect old JS state: after Back from the cart or
    // checkout page (or changes in another tab) the badge and drawer would be
    // stale, so refetch.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        fetched = false;
        refreshCart(true).catch(() => {});
      }
    });
  }

  // --- Global delegation: gallery slides, buy buttons + cart-open triggers ---
  document.addEventListener('click', (e) => {
    const slideBtn = e.target.closest('[data-bh-slide-to]');
    if (slideBtn) {
      const carousel = slideBtn.closest('[data-bh-carousel]');
      if (carousel) {
        const idx = slideBtn.dataset.bhSlideTo;
        carousel.querySelectorAll('[data-bh-slide]').forEach((s) => {
          s.classList.toggle('is-active', s.dataset.bhSlide === idx);
        });
        carousel.querySelectorAll('[data-bh-slide-to]').forEach((b) => {
          b.classList.toggle('is-active', b === slideBtn);
          b.setAttribute('aria-pressed', b === slideBtn ? 'true' : 'false');
        });
      }
      return;
    }

    const buy = e.target.closest('[data-bh-buy]');
    if (buy && drawer) {
      // Keep repeated clicks inside the enhanced flow while a cart operation
      // is running; never fall through to the native cart-page link.
      if (busy) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      setBusy(true);
      setTriggerBusy(buy, true);
      clearError();
      cartSeq += 1; // any in-flight plain refresh now holds a pre-add snapshot
      const baseCart = copyCart();
      const addMeta = addMetaFromButton(buy);
      if (addMeta) render(cartWithAddition(baseCart, addMeta));
      setLoading(!addMeta);
      openDrawer(buy);
      addItem({
        id: parseInt(buy.dataset.variantId, 10),
        quantity: parseInt(buy.dataset.quantity || '1', 10),
      })
        .then((added) => {
          if (addMeta) {
            render(cartWithAddition(baseCart, addMeta, addedLineFromResponse(added, addMeta.id)));
          }
          setLoading(false);
          setTriggerBusy(buy, false);
          setBusy(false);
          refreshCart(true).catch((e2) => showError(e2.message));
        })
        .catch((e2) => {
          render(baseCart);
          showError(e2.message);
          setLoading(false);
          setTriggerBusy(buy, false);
          setBusy(false);
        });
      return;
    }

    const opener = e.target.closest('[data-bh-cart-open]');
    if (opener && drawer) {
      e.preventDefault();
      openDrawer(opener);
    }
  });

  // Product form: intercept submit in drawer mode
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-bh-atc-form]');
    if (!form) return;
    const discountCodes = form.dataset.bhDiscountCodes || '';
    const directCheckout = form.hasAttribute('data-bh-direct-checkout');
    const useDrawer = Boolean(drawer && !directCheckout);
    if (!drawer && !discountCodes && !directCheckout) return;
    if (busy) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    const errRegion = form.querySelector('[data-bh-form-error]');
    const trigger = e.submitter || form.querySelector('[type="submit"]');
    if (errRegion) errRegion.hidden = true;
    setBusy(true);
    setTriggerBusy(trigger, true);
    if (directCheckout) {
      try {
        const checkoutUrl = checkoutPermalink(form, discountCodes);
        window.location.assign(checkoutUrl);
        setTriggerBusy(trigger, false);
        setBusy(false);
      } catch (e2) {
        showError(e2.message);
        if (errRegion) {
          errRegion.textContent = e2.message;
          errRegion.hidden = false;
        }
        setTriggerBusy(trigger, false);
        setBusy(false);
      }
      return;
    }
    cartSeq += 1; // any in-flight plain refresh now holds a pre-add snapshot
    const baseCart = copyCart();
    const addMeta = addMetaFromForm(form);
    if (addMeta && useDrawer) render(cartWithAddition(baseCart, addMeta));
    setLoading(useDrawer && !addMeta);
    if (useDrawer) openDrawer(trigger);
    let itemAdded = false;
    addForm(form)
      .then((added) => {
        itemAdded = true;
        if (addMeta && useDrawer) {
          render(cartWithAddition(baseCart, addMeta, addedLineFromResponse(added, addMeta.id)));
        }
        if (discountCodes) return updateDiscounts(discountCodes);
        return null;
      })
      .then((discountedCart) => {
        if (discountedCart && useDrawer) {
          cartSeq += 1;
          fetched = true;
          render(discountedCart);
        }
        setLoading(false);
        setTriggerBusy(trigger, false);
        setBusy(false);
        if (directCheckout || !drawer) {
          const returnTo = form.querySelector('[name="return_to"]')?.value || `${i18n.localeRoot || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}checkout`;
          window.location.assign(returnTo);
          return;
        }
        refreshCart(true).catch((e2) => showError(e2.message));
      })
      .catch((e2) => {
        if (itemAdded && useDrawer) refreshCart(true).catch(() => {});
        else if (useDrawer) render(baseCart);
        showError(e2.message);
        if (errRegion) {
          errRegion.textContent = e2.message;
          errRegion.hidden = false;
        }
        setLoading(false);
        setTriggerBusy(trigger, false);
        setBusy(false);
      });
  });

  // --- Product purchase enhancements (mode-independent) ---
  // Delegated and scoped to the button's own form so the same hooks work in
  // every purchase UI (product page, monitor section, device hero).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-bh-qty-step]');
    if (!btn) return;
    const scope = btn.closest('form') || document;
    const input = scope.querySelector('input[name="quantity"]');
    if (!input) return;
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.bhQtyStep, 10));
  });

  document.addEventListener('change', (e) => {
    const select = e.target.closest('select[name="id"]');
    if (!select || !select.form) return;
    const scope = select.form.closest('[data-bh-buy-scope]') || select.form.closest('section') || document;
    const variantsEl = scope.querySelector('[data-bh-variants]') || document.querySelector('[data-bh-variants]');
    if (!variantsEl) return;
    let variants = [];
    try {
      variants = JSON.parse(variantsEl.textContent);
    } catch (err) {
      variants = [];
    }
    const v = variants.find((x) => String(x.id) === select.value);
    if (!v) return;
    select.form.dataset.bhProductPrice = v.price;
    select.form.dataset.bhVariantTitle = v.public_title || v.title || 'Default Title';
    if (v.featured_image?.src) select.form.dataset.bhProductImage = v.featured_image.src;
    const priceEl = scope.querySelector('[data-bh-price]');
    if (priceEl) priceEl.textContent = money(v.price);
    const barPriceEls = scope.querySelectorAll('[data-bh-bar-price]');
    barPriceEls.forEach((barPriceEl) => {
      barPriceEl.textContent = money(v.price);
    });
    const compareEls = scope.querySelectorAll('[data-bh-compare-price], [data-bh-bar-compare-price]');
    const onSale = v.compare_at_price && v.compare_at_price > v.price;
    compareEls.forEach((compareEl) => {
      compareEl.hidden = !onSale;
      if (onSale) compareEl.textContent = money(v.compare_at_price);
    });
    const discountEls = scope.querySelectorAll('[data-bh-discount], [data-bh-bar-discount]');
    discountEls.forEach((discountEl) => {
      discountEl.hidden = !onSale;
      if (onSale) {
        const percentage = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
        discountEl.textContent = percentage + '% ' + (discountEl.dataset.label || i18n.discountOff || 'off');
      }
    });
    const atc = select.form.querySelector('[type="submit"][data-label-add]');
    if (atc) {
      atc.disabled = false;
      atc.textContent = atc.dataset.labelAdd;
    }
  });

  // --- Sticky buy bar (device hero) ---
  // Lives here rather than bh-animations.js: the bar must work under
  // prefers-reduced-motion, where the reveal system bails out entirely.
  const stickyBar = document.querySelector('[data-bh-sticky-bar]');
  const stickySection = stickyBar && document.getElementById('buy');
  const stickyPurchase = stickySection?.querySelector('.bh-device-hero__buy-row');
  const stickyMobile = window.matchMedia('(max-width: 749px)');
  let stickyObserver = null;

  const setStickyVisible = (show) => {
    if (!stickyBar) return;
    stickyBar.classList.toggle('is-visible', show);
    document.body.classList.toggle('bh-stickybar-visible', show);
  };

  const watchStickyCart = () => {
    if (!stickyBar || !stickySection || !('IntersectionObserver' in window)) return;
    if (stickyObserver) stickyObserver.disconnect();

    // Reveal the bar after the primary purchase controls scroll above the
    // viewport on both desktop and mobile, then hide it again when they return.
    const watched = stickyPurchase || stickySection;
    stickyBar.hidden = false;
    setStickyVisible(false);

    stickyObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      const hasPassedAbove = entry.boundingClientRect.bottom <= 0;
      const show = !entry.isIntersecting && hasPassedAbove;
      setStickyVisible(show);
    });
    stickyObserver.observe(watched);
  };

  if (stickyBar && stickySection) {
    watchStickyCart();
    if (stickyMobile.addEventListener) stickyMobile.addEventListener('change', watchStickyCart);
    else if (stickyMobile.addListener) stickyMobile.addListener(watchStickyCart);
  }

  document.querySelectorAll('[data-bh-gallery-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const main = document.querySelector('[data-bh-gallery-main]');
      if (main && thumb.dataset.src) {
        // The main image ships with a srcset which would override src
        main.removeAttribute('srcset');
        main.removeAttribute('sizes');
        main.src = thumb.dataset.src;
        document
          .querySelectorAll('[data-bh-gallery-thumb]')
          .forEach((t) => t.classList.toggle('is-active', t === thumb));
      }
    });
  });
})();
